import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL, TBS_TENANT_ID } from "../../config";
import type {
  Payment,
  PaymentInput,
  PaymentList,
  PaymentListParams,
} from "../../types/tbs/payments";

// Tiendas Biai — payments.

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false) continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

export const tbsPaymentsApi = createApi({
  reducerPath: "tbsPaymentsApi",
  tagTypes: ["TbsPayment", "TbsInvoice"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      if (TBS_TENANT_ID) headers.set("X-Tenant-Id", TBS_TENANT_ID);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPayments: builder.query<PaymentList, PaymentListParams | void>({
      query: (params) => `/v1/payments${qs({ ...(params ?? {}) })}`,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((p) => ({ type: "TbsPayment" as const, id: p.id })),
              { type: "TbsPayment" as const, id: "LIST" },
            ]
          : [{ type: "TbsPayment" as const, id: "LIST" }],
    }),

    getPayment: builder.query<Payment, string>({
      query: (id) => `/v1/payments/${id}`,
      providesTags: (_r, _e, id) => [{ type: "TbsPayment", id }],
    }),

    /**
     * **The caller supplies the idempotency key**, exactly as `createInvoice` does, and for the same
     * reason: it has to be *the same key* on a retry. A key minted here would be new on every
     * attempt, which is precisely the duplicate the mechanism exists to prevent — and the duplicate
     * here is the customer's money recorded twice.
     */
    createPayment: builder.mutation<Payment, { body: PaymentInput; idempotencyKey: string }>({
      query: ({ body, idempotencyKey }) => ({
        url: "/v1/payments",
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body,
      }),
      // A payment changes invoice balances, so the invoice list is stale the moment this returns.
      // The tag is declared in **both** slices deliberately: RTK Query scopes tags per API, so
      // `tbsInvoicesApi` will not see this one. The `PaymentListPage` dispatches
      // `tbsInvoicesApi.util.invalidateTags` for that — a cross-slice invalidation a tag cannot
      // express, the same arrangement `useAlegraSync` needed.
      invalidatesTags: [
        { type: "TbsPayment", id: "LIST" },
        { type: "TbsInvoice", id: "LIST" },
      ],
    }),

    /** Admin. The row survives with `status: "void"` — money movements are never deleted. */
    voidPayment: builder.mutation<Payment, string>({
      query: (id) => ({ url: `/v1/payments/${id}/void`, method: "POST" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "TbsPayment", id },
        { type: "TbsPayment", id: "LIST" },
        { type: "TbsInvoice", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetPaymentsQuery,
  useGetPaymentQuery,
  useCreatePaymentMutation,
  useVoidPaymentMutation,
} = tbsPaymentsApi;
